//#region types
import type { AxiosResponse } from "axios";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import type { ForwardableCost, ForwardedCostMissingReason } from "models/utilTypes/costForwardingTypes";
import type { ForwardedCostsSelection } from "models/utilTypes/forwardableCostTypes";
import type { UploadedFile } from "models/utilTypes/fileUtilsTypes";
//#endregion
import { MaxFileSize } from "utils/constants/fileConstants";
import FileHelper from "utils/helpers/fileHelper";
import ForwardedCostHelper from "utils/helpers/forwardedCostHelper";

export interface RateDependencies {
  /** The currency endpoint's rate: SEK per unit of the given currency. */
  getExchangeRate: (currency: string, date: Date) => Promise<number>;
  getDefaultUnitId: () => Promise<number>;
}

/** Fetches what rows added by this selection need; nothing when every ticked cost is already on the invoice. */
export const getRatesForNewRows = async (
  selection: ForwardedCostsSelection,
  invoice: { currency?: string; invoiceDate?: Date; addedCostIds: number[] },
  deps: RateDependencies
) => {
  const alreadyAdded = new Set(invoice.addedCostIds);
  if (!selection.selectedCostIds.some((id) => !alreadyAdded.has(id))) return { exchangeRate: 1, unitId: 0 };

  const [exchangeRate, unitId] = await Promise.all([
    ForwardedCostHelper.needsExchangeRate(invoice.currency)
      ? deps.getExchangeRate(invoice.currency!, invoice.invoiceDate ?? new Date())
      : Promise.resolve(1),
    deps.getDefaultUnitId(),
  ]);
  if (!(exchangeRate > 0)) throw new Error(`No usable exchange rate for ${invoice.currency}`);
  return { exchangeRate, unitId };
};

/**
 * Starts loading a customer's costs; the returned cancel drops a response that arrives after the customer changed.
 * onLoaded gets the same check, for work it still has to wait for.
 */
export const loadForwardableCosts = (
  customerId: number,
  fetch: (customerId: number) => Promise<ForwardableCost[]>,
  onLoaded: (costs: ForwardableCost[], isCurrent: () => boolean) => void | Promise<void>
) => {
  let isCurrent = true;
  fetch(customerId)
    .then((costs) => isCurrent && onLoaded(costs, () => isCurrent))
    .catch(() => undefined);
  return () => {
    isCurrent = false;
  };
};

export interface CostFileDependencies {
  getImagesMeta: (
    entityId: number,
    type: WintSuperkollAPIModel.ImageType
  ) => Promise<WintSuperkollAPIModel.Attachments>;
  getImageFile: (id: number | string, type: WintSuperkollAPIModel.ImageType) => Promise<AxiosResponse<void>>;
}

const getCostDocument = (cost: ForwardableCost) => {
  if (cost.receiptId !== undefined) return { id: cost.receiptId, type: WintSuperkollAPIModel.ImageType.Receipt };
  if (cost.incomingInvoiceId !== undefined)
    return { id: cost.incomingInvoiceId, type: WintSuperkollAPIModel.ImageType.IncomingInvoice };
  return undefined;
};

// Receipt and supplier-invoice files have integer ids that the meta sends as strings, and the download route
// picks its integer or guid overload by the id's shape.
const toFileId = (attachmentId: string) => (/^\d+$/.test(attachmentId) ? Number(attachmentId) : attachmentId);

const getAttachmentFieldTypes = () => {
  // The invoice's attachment field sets no type options of its own.
  const options = FileHelper.getFileUploaderTypeOptions({});
  return FileHelper.getAcceptedFileTypes(options.allowAnyContentType, options.allowAnyImageType, options.allowedTypes);
};

// Core answers a document the user may not view the same way as a deleted one, and a listed cost's document exists.
const NoAccessErrorCodes = ["ReceiptNotAvailableOrUnAuthorized", "IncomingInvoiceNotAvailableOrUnAuthorized"];

const isNoAccess = (error: unknown) => {
  const { status, codes } = (error ?? {}) as { status?: number; codes?: string[] };
  return status === 403 || NoAccessErrorCodes.some((code) => codes?.includes(code));
};

// A request that never settles would keep the save waiting on the cost's files.
export const CostFileFetchTimeoutMs = 60_000;

const startDeadline = () => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error("Timed out")), CostFileFetchTimeoutMs);
  });
  return {
    race: <T>(request: Promise<T>) => Promise.race([request, expired]),
    clear: () => clearTimeout(timer),
  };
};

const toUploadedFile = (
  sourceDocument: string,
  meta: WintSuperkollAPIModel.AttachmentMeta,
  response: AxiosResponse<void>
): UploadedFile => {
  // axios types header values as AxiosHeaderValue | undefined; the API sends single strings.
  const storedName = FileHelper.getFileNameFromContentDisposition(
    response.headers["content-disposition"] as string | undefined
  );
  const contentType = FileHelper.normalizeContentType(
    storedName,
    (response.headers["content-type"] as string | undefined) || meta.contentType
  );
  const extension = FileHelper.getFileExtension(contentType);
  const fallbackName = extension ? `${meta.attachmentId}.${extension}` : meta.attachmentId;
  const data = response.data as unknown as ArrayBuffer;

  return {
    fileName: `${sourceDocument} - ${storedName?.trim() || fallbackName}`,
    contentType,
    fileSize: data.byteLength,
    data: FileHelper.readFileAsBase64String(data),
  };
};

/**
 * The cost's source files as new invoice attachments. missing is set when any file is left out: noAccess when the
 * user may not view the document, otherwise notAttached (a fetch failed or missed the deadline, the document has no
 * files, or the attachment field would reject the file's type or size).
 */
export const fetchForwardedCostFiles = async (
  cost: ForwardableCost,
  deps: CostFileDependencies
): Promise<{ files: UploadedFile[]; missing?: ForwardedCostMissingReason }> => {
  const document = getCostDocument(cost);
  if (!document) return { files: [], missing: "notAttached" };

  const deadline = startDeadline();
  try {
    let metas: WintSuperkollAPIModel.AttachmentMeta[];
    try {
      metas = (await deadline.race(deps.getImagesMeta(document.id, document.type))).attachmentsMeta ?? [];
    } catch (error) {
      return { files: [], missing: isNoAccess(error) ? "noAccess" : "notAttached" };
    }
    if (metas.length === 0) return { files: [], missing: "notAttached" };

    const allowedTypes = getAttachmentFieldTypes();
    const downloads = await Promise.allSettled(
      metas.map((meta) =>
        meta.attachmentId
          ? deadline.race(deps.getImageFile(toFileId(meta.attachmentId), document.type))
          : Promise.reject(new Error("File has no id"))
      )
    );

    const files = downloads.flatMap((download, index) => {
      if (download.status === "rejected") return [];
      if ((download.value.data as unknown as ArrayBuffer).byteLength > MaxFileSize) return [];
      const file = toUploadedFile(cost.sourceDocument, metas[index], download.value);
      return FileHelper.allowUpload(file.fileName, file.contentType, allowedTypes) ? [file] : [];
    });

    return { files, missing: files.length < metas.length ? "notAttached" : undefined };
  } finally {
    deadline.clear();
  }
};
